import React from "react";
import { CustomStatCard } from "subframe/components/CustomStatCard";
import * as SubframeCore from "@subframe/core";
import { TaskCenterFilter, TaskCenterTab } from "modules/Retention/types";
import { useQuery } from "@tanstack/react-query";
import { getEscalationsSummary } from "modules/Retention/services/retention";
import endpoints from "modules/Retention/constants/endpoints";
import { getDateRangeFromTimeRange } from "utils/helper";


interface TaskCenterStatsProps {
  showAllStats?: boolean;
  status: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  filters?: TaskCenterFilter;
  onFilterChange?: (filters: TaskCenterFilter) => void;
  loading?: boolean;
  activeTab: TaskCenterTab;
}

const TaskCenterStatsSkeleton: React.FC<{ showAllStats?: boolean }> = ({ showAllStats = true }) => {
  if (showAllStats) {
    return (
      <div className="flex w-full flex-col md:flex-row items-start md:items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
          <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
          <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
          <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
          <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex w-full flex-col md:flex-row items-start md:items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
          <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
          <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
        </div>
      </div>
    );
  }
};

export const TaskCenterStatsCards: React.FC<TaskCenterStatsProps> = ({
  showAllStats = true,
  status,
  setStatus,
  filters,
  onFilterChange,
  activeTab,
}) => {

  // Prepare API parameters for summary (excluding pagination and status)
  const summaryParams: any = {
    type: activeTab,
  };

  // Only include location if it's not "all"
  if (filters?.branch && filters.branch !== "all") {
    summaryParams.location = filters.branch;
  }

  // Include date range if available
  if (filters?.timeRange) {
    const dateRange = getDateRangeFromTimeRange(filters.timeRange);
    Object.assign(summaryParams, dateRange);
  }

  const { data: displayStats, isLoading } = useQuery({
    queryKey: [endpoints.escalationsSummary, activeTab, filters?.timeRange, filters?.branch],
    queryFn: () => getEscalationsSummary(summaryParams),
    select: (res:any) => res?.result,
  });

  const handleCardClick = (statusValue: string) => {
    setStatus(statusValue === status ? '' : statusValue);
    // Reset all filters when status changes
    if (onFilterChange && filters) {
      onFilterChange({
        ...filters,
        timeRange: '0', // Reset to "All time"
        branch: 'all',  // Reset to "All branch"
        rankBy: 'escalation_time' // Reset to default
      });
    }
  };

  const getCardWrapperClass = (statusValue: string, colorClass: string) => {
    return status === statusValue ? `border border-solid ${colorClass} rounded-md` : '';
  };

  if (isLoading || !displayStats) {
    return <TaskCenterStatsSkeleton showAllStats={showAllStats} />;
  }

  if (showAllStats) {
    // Show all cards for Escalation Analysis
    return (
      <div className="flex w-full flex-col md:flex-row items-start md:items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="transition-all duration-200"
            onClick={() => handleCardClick('')}
          >
            <CustomStatCard
              className="h-20 md:h-24 w-28 md:w-36 flex-none cursor-pointer"
              variant="total"
              title="Total"
              value={displayStats.total}
              secondaryValue=""
              secondaryLabel=""
              loading={false}
            />
          </div>
          <div
            className={`transition-all duration-200 ${getCardWrapperClass('at_risk', 'border-error-600')}`}
            onClick={() => handleCardClick('at_risk')}
          >
            <CustomStatCard
              className="h-20 md:h-24 w-28 md:w-36 flex-none cursor-pointer"
              variant="at-risk"
              title="At-risk"
              value={displayStats.at_risk}
              secondaryValue=""
              secondaryLabel=""
            />
          </div>
          <div
            className={`transition-all duration-200 ${getCardWrapperClass('in_progress', 'border-warning-600')}`}
            onClick={() => handleCardClick('in_progress')}
          >
            <CustomStatCard
              className="h-20 md:h-24 w-28 md:w-36 flex-none cursor-pointer"
              variant="escalated"
              title="In Progress"
              value={displayStats.in_progress}
              secondaryValue=""
              secondaryLabel=""
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className={`transition-all duration-200 ${getCardWrapperClass('resolved', 'border-success-600')}`}
            onClick={() => handleCardClick('resolved')}
          >
            <CustomStatCard
              className="h-20 md:h-24 w-28 md:w-36 flex-none cursor-pointer"
              variant="resolved"
              title="Resolved"
              value={displayStats.resolved}
              secondaryValue=""
              secondaryLabel=""
            />
          </div>
          <div
            className={`transition-all duration-200 ${getCardWrapperClass('dismissed', 'border-neutral-600')}`}
            onClick={() => handleCardClick('dismissed')}
          >
            <CustomStatCard
              className="h-20 md:h-24 w-28 md:w-36 flex-none cursor-pointer"
              variant="variation"
              title="Dismissed"
              value={displayStats.dismissed}
              secondaryValue=""
              secondaryLabel=""
            />
          </div>
          <SubframeCore.HoverCard.Root>
            <SubframeCore.HoverCard.Trigger asChild={true}>
              <div
                className={`transition-all duration-200 ${getCardWrapperClass('expired', 'border-gray-900')}`}
                onClick={() => handleCardClick('expired')}
              >
                <CustomStatCard
                  className="h-20 md:h-24 w-28 md:w-36 flex-none cursor-pointer"
                  variant="overdue"
                  title="Expired"
                  value={displayStats.expired}
                  secondaryValue=""
                  secondaryLabel=""
                />
              </div>
            </SubframeCore.HoverCard.Trigger>
            <SubframeCore.HoverCard.Portal>
              <SubframeCore.HoverCard.Content
                side="top"
                align="center"
                sideOffset={4}
                asChild={true}
              >
                <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-3 shadow-lg">
                  <span className="text-body font-body text-default-font">
                    Expired: Tickets unresolved for over 30 days. Threshold can
                    be customized by your organization.
                  </span>
                </div>
              </SubframeCore.HoverCard.Content>
            </SubframeCore.HoverCard.Portal>
          </SubframeCore.HoverCard.Root>
        </div>
      </div>
    );
  } else {
    // Show only Total, At-risk, and In Progress for Escalations tab
    return (
      <div className="flex w-full flex-col md:flex-row items-start md:items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="transition-all duration-200"
            onClick={() => handleCardClick('')}
          >
            <CustomStatCard
              className="h-20 md:h-24 w-28 md:w-36 flex-none cursor-pointer"
              variant="total"
              title="Total"
              value={displayStats.total}
              secondaryValue=""
              secondaryLabel=""
              loading={false}
            />
          </div>
          <div
            className={`transition-all duration-200 ${getCardWrapperClass('at_risk', 'border-error-600')}`}
            onClick={() => handleCardClick('at_risk')}
          >
            <CustomStatCard
              className="h-20 md:h-24 w-28 md:w-36 flex-none cursor-pointer"
              variant="at-risk"
              title="Action needed"
              value={displayStats.at_risk}
              secondaryValue=""
              secondaryLabel=""
            />
          </div>
          <div
            className={`transition-all duration-200 ${getCardWrapperClass('in_progress', 'border-warning-600')}`}
            onClick={() => handleCardClick('in_progress')}
          >
            <CustomStatCard
              className="h-20 md:h-24 w-28 md:w-36 flex-none cursor-pointer"
              variant="escalated"
              title="In Progress"
              value={displayStats.in_progress}
              secondaryValue=""
              secondaryLabel=""
            />
          </div>
        </div>
      </div>
    );
  }
}; 